"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { useState, useCallback, useRef } from "react";
import { Swiper as SwiperType } from "swiper";
import SwiperButton from "../buttons/SwiperButton";
import ProductCard from "../cards/productCard/ProductCard";
import ViewAllButton from "../buttons/ViewAllButton";
import { Product } from "@/app/lib/features/product/product.types";
import { useHoverGallery } from "@/app/hooks/useHoverGallery";
import { reviveSwiperOnResize } from "@/app/utils/swiperResize";
import { trackSectionClick } from "@/app/lib/tracking/interactions";

interface ProductSwiperProps {
  products: Product[];
  sectionId?: number;
  viewAllSlug?: string;
  showViewAll?: boolean;
  maxSlidesPerView?: 4 | 6;
  priorityCount?: number;
}

const breakpointsFor4 = {
  0: { slidesPerView: 1.6, spaceBetween: 10 },
  480: { slidesPerView: 2.2, spaceBetween: 10 },
  640: { slidesPerView: 2.6, spaceBetween: 12 },
  768: { slidesPerView: 3.2, spaceBetween: 12 },
  1024: { slidesPerView: 4.2, spaceBetween: 14 },
  1280: { slidesPerView: 4.2, spaceBetween: 14 },
  1536: { slidesPerView: 4.2, spaceBetween: 16 },
};

const breakpointsFor6 = {
  0: { slidesPerView: 2.2, spaceBetween: 10 },
  480: { slidesPerView: 2.8, spaceBetween: 10 },
  640: { slidesPerView: 3.2, spaceBetween: 12 },
  768: { slidesPerView: 4.2, spaceBetween: 12 },
  1024: { slidesPerView: 5.2, spaceBetween: 14 },
  1280: { slidesPerView: 6.2, spaceBetween: 14 },
  1536: { slidesPerView: 6.2, spaceBetween: 16 },
};

/**
 * Carousel'in yatay konumu — oturum boyunca sectionId basina saklanir.
 *
 * Sorun: urun detayina girip geri donuldugunde dikey scroll korunuyor ama
 * carousel basa doniyordu; 4. karttan giren kullanici 1. kartin yanina
 * donuyordu. Geri navigasyonda React agaci yeniden kuruluyor, Swiper sifirdan
 * init oluyor.
 *
 * NEDEN geri/ileri navigasyonuyla SINIRLANMADI: projede bunun icin
 * consumeBackForwardNavigation() var ama o bayragi TUKETIYOR — ilk okuyan
 * alir, digerleri false gorur. Ana sayfada yan yana 7 carousel var, hepsi
 * ayni anda mount oluyor; yalnizca ilki dogru cevabi alirdi. Tuketmeyen yeni
 * bir primitif eklemek yerine kapsam oturuma baglandi: ayni sekmede geri
 * donuldugunde konum korunur, yeni sekmede/yeni oturumda sifirdan baslar.
 */
const POSITION_KEY = (sectionId: number) => `swiper_pos_${sectionId}`;

const readSavedIndex = (sectionId?: number): number => {
  if (sectionId === undefined || typeof window === "undefined") return 0;
  try {
    const raw = sessionStorage.getItem(POSITION_KEY(sectionId));
    const parsed = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
};

const saveIndex = (sectionId: number | undefined, index: number): void => {
  if (sectionId === undefined) return;
  try {
    // 0 = baslangic konumu; kaydetmek yerine anahtari temizlemek
    // sessionStorage'i sisirmez ve "kayit yok" ile ayni anlama gelir.
    if (index > 0) sessionStorage.setItem(POSITION_KEY(sectionId), String(index));
    else sessionStorage.removeItem(POSITION_KEY(sectionId));
  } catch {}
};

const ProductSwiper = ({
  products,
  sectionId,
  viewAllSlug,
  showViewAll = true,
  maxSlidesPerView = 6,
  priorityCount,
}: ProductSwiperProps) => {
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);
  // Desktop: çoklu görsel + hover galerisi. Mobil/dokunmatik: tek ana görsel.
  const hoverGallery = useHoverGallery();

  // Hydration öncesi native scroll pozisyonu (bkz. globals.css "pre-init NATIVE
  // yatay scroll"). onBeforeInit, Swiper'ın addClasses()'ından önce çalışır —
  // .swiper-initialized henüz eklenmediği için wrapper hâlâ scroll container ve
  // scrollLeft okunabilir. Init sonrası wrapper scroll'u kaybeder, scrollLeft
  // sıfırlanır; o yüzden değeri burada yakalamak zorundayız.
  const preInitScrollRef = useRef(0);

  const handleBeforeInit = useCallback((swiper: SwiperType) => {
    preInitScrollRef.current = swiper.wrapperEl?.scrollLeft ?? 0;
  }, []);

  const updateGradients = useCallback((swiper: SwiperType): void => {
    setShowLeftGradient(!swiper.isBeginning);
    setShowRightGradient(!swiper.isEnd);
  }, []);

  /** Kaydedilmis konum bir kez uygulanir; sonraki resize'lar tekrar denemez. */
  const restoredRef = useRef(false);

  const restoreSavedIndex = useCallback(
    (swiper: SwiperType): boolean => {
      if (restoredRef.current) return false;

      const saved = readSavedIndex(sectionId);
      if (saved <= 0) {
        restoredRef.current = true;
        return false;
      }

      // content-visibility ile ertelenen section'larda Swiper 0 genislikte init
      // olabiliyor (bkz. swiperResize.ts). O anda slidesGrid guvenilir degil ve
      // slideTo yanlis translate uretir — bolum gercek genislige kavustugunda
      // onResize bu fonksiyonu yeniden cagiriyor.
      if (!swiper.el?.offsetWidth) return false;

      restoredRef.current = true;
      const lastIndex = Math.max(swiper.slides.length - 1, 0);
      swiper.slideTo(Math.min(saved, lastIndex), 0, false);
      return true;
    },
    [sectionId],
  );

  const handleSlideChange = useCallback(
    (swiper: SwiperType): void => {
      updateGradients(swiper);
      saveIndex(sectionId, swiper.activeIndex);
    },
    [sectionId, updateGradients],
  );

  const handleResize = useCallback(
    (swiper: SwiperType): void => {
      reviveSwiperOnResize(swiper);
      // Olculer yeni oturdu: ertelenmis bolumde atlanan restorasyon burada
      // tamamlanir. slideTo callback calistirmadigi icin gradyanlari elle tazele.
      if (restoreSavedIndex(swiper)) updateGradients(swiper);
    },
    [restoreSavedIndex, updateGradients],
  );

  const handleSwiper = useCallback(
    (swiper: SwiperType) => {
      const scrolled = preInitScrollRef.current;
      preInitScrollRef.current = 0;

      // İki kaynak da başlangıç konumunu belirlemek istiyor; öncelik sırası:
      //
      //   1) Kullanıcının BU sayfa görüntülemesinde, hidrasyonu beklemeden
      //      yaptığı native kaydırma. Az önceki bir jest, önceki ziyaretten
      //      kalan kayıttan daha günceldir.
      //   2) sessionStorage'daki kayıtlı konum (geri dönüşte kaldığı yer).
      //
      // Jest varsa kaydı hiç uygulamıyoruz: restoredRef'i burada işaretlemek,
      // bölüm sonradan görünür olup onResize tetiklendiğinde restorasyonun
      // kullanıcının konumunu ezmesini de engelliyor.
      if (scrolled > 0) restoredRef.current = true;
      else restoreSavedIndex(swiper);

      // Native kaydırma varsa: Swiper'ın başa dönmesini engelle, native
      // offset'e en yakın slide'a animasyonsuz atla.
      if (scrolled > 0) {
        if (swiper.wrapperEl) swiper.wrapperEl.scrollLeft = 0;
        const grid = swiper.slidesGrid ?? [];
        let closest = 0;
        let bestDistance = Infinity;
        grid.forEach((offset, index) => {
          const distance = Math.abs(offset - scrolled);
          if (distance < bestDistance) {
            bestDistance = distance;
            closest = index;
          }
        });
        if (grid.length > 0) swiper.slideTo(closest, 0, false);
      }

      updateGradients(swiper);
    },
    [restoreSavedIndex, updateGradients],
  );

  if (!products || products.length === 0) return <></>;

  const breakpoints = maxSlidesPerView === 4 ? breakpointsFor4 : breakpointsFor6;
  const swiperClass = maxSlidesPerView === 4 ? "product-swiper-4" : "product-swiper-6";

  return (
    <div className="relative">
      <Swiper
        className={`product-swiper ${swiperClass} relative`}
        spaceBetween={10}
        slidesPerView={2.2}
        onBeforeInit={handleBeforeInit}
        onSwiper={handleSwiper}
        onSlideChange={handleSlideChange}
        breakpoints={breakpoints}
        // Varsayılan "wrapper" hedefi, .swiper-wrapper'ın width:100% kutusunun
        // dışında kalan alanlarda (kartlar arası spaceBetween boşlukları, kenarlar)
        // pointerdown'ı eliyor — slide'lar wrapper kutusundan taşar ve wrapper
        // translate ile kayar. "container" ile boşluklar dahil tüm yüzey sürüklenir.
        touchEventsTarget="container"
        noSwiping={true}
        noSwipingClass="swiper-no-swiping"
        watchSlidesProgress={false}
        updateOnWindowResize={false}
        // content-visibility ile ertelenen section'larda 0 genişlikte init olan
        // Swiper'ı kurtarır — bkz. swiperResize.ts
        onResize={handleResize}
        resistanceRatio={0}
      >
        {products.map((product, index) => (
          <SwiperSlide key={product.id} className="h-auto! flex!">
            <ProductCard
              product={product}
              // Görsel alanı artık swiper-no-swiping taşımıyor → kartın tamamından
              // (görsel dahil) carousel sürüklenebilir. Desktop'ta hover galerisi
              // korunur, mobilde tek ana görsele düşer.
              carousel={hoverGallery ? "gallery" : "single"}
              // priorityCount=0 → priority hiçbir karta verilmez (isFirst=false durumu).
              // priorityCount=undefined → maxSlidesPerView kadar karta priority verilir.
              priority={priorityCount !== undefined
                ? index < priorityCount
                : index < (maxSlidesPerView ?? 6)}
            />
          </SwiperSlide>
        ))}

        {showViewAll && viewAllSlug && (
          <SwiperSlide className="h-auto! flex!">
            <ViewAllButton
              slug={viewAllSlug}
              onClick={() => { if (sectionId) trackSectionClick(sectionId); }}
            />
          </SwiperSlide>
        )}

        <div className="absolute z-50 top-1/2 -translate-y-1/2 left-0 w-full items-center justify-between px-3 pointer-events-none hidden xl:flex">
          <SwiperButton position="left" slideMode="group" />
          <SwiperButton position="right" slideMode="group" />
        </div>

        {/* <div
          className={`absolute top-0 left-0 z-20 w-5 sm:w-10 xl:w-14 h-full bg-linear-to-r from-white to-transparent transition-opacity duration-300 pointer-events-none ${
            showLeftGradient ? "opacity-100" : "opacity-0"
          }`}
        />
        <div
          className={`absolute top-0 right-0 z-20 w-5 sm:w-10 xl:w-14 h-full bg-linear-to-l from-white to-transparent transition-opacity duration-300 pointer-events-none ${
            showRightGradient ? "opacity-100" : "opacity-0"
          }`}
        /> */}
      </Swiper>
    </div>
  );
};

export default ProductSwiper;